import React, { useState } from 'react';
import { Screen } from 'src/components/screen';
import { DashboardHeader } from '../components/header/header';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  ListItem,
  Typography,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
} from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  container,
  infoContainer,
  listItem,
  gridContainer,
  noReviewsContainer,
  noReviewsTitle,
  noReviewsSubtitle,
  progressContainer,
  titleContainer,
  switchPrimary,
} from './styles';
import { useWindowDimensions } from 'src/utils/ui/dimensions';
import { useReviews } from 'src/api/hooks/use-reviews';
import { useAppAuth } from 'src/context/use-app-auth';
import { ReviewCard } from './review-item';
import { reviewLabels } from 'src/text/locales/default-en';
import { ReviewDialog } from './review-dialog';
import { Review } from 'src/api/types/types';
import { conditionalRender } from 'src/utils/ui/conditional-render';
import { ReactComponent as IconNoReviews } from 'src/assets/svg/icon-no-reviews.svg';
import { generateButton } from './review-item/styles';

const ReviewsScreen: React.FC = () => {
  const { isMobile, height } = useWindowDimensions();
  const { location, isAdmin } = useAppAuth();
  const navigate = useNavigate();
  const [noReplyOnly, setNoReplyOnly] = useState(true);
  const { reviews, loading, resetReviews, loadMore, hasMore, loadingMore } =
    useReviews({
      locationId: location?.key,
      noReplyOnly,
    });

  const [resetLoading, setResetLoading] = useState(false);

  const [selectedReview, setSelectedReview] = useState<Review | undefined>();

  const handleOnReviewPress = (review: Review) => {
    setSelectedReview(review);
  };

  const handleResetReviews = async () => {
    setResetLoading(true);
    await resetReviews();
    setResetLoading(false);
  };

  return (
    <Screen>
      <Box sx={{ ...container, minHeight: height }}>
        <DashboardHeader />

        <Box
          mt={8}
          sx={{
            ...titleContainer,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            rowGap: isMobile ? 2 : 0,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
            <Typography variant="h4">{reviewLabels.title}</Typography>

            <Tooltip title={reviewLabels.info} placement="right" arrow>
              <IconButton size="small" aria-label="About reviews">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              columnGap: isMobile ? 0 : 2,
              rowGap: isMobile ? 1.5 : 0,
              width: isMobile ? '100%' : 'auto',
              mt: isMobile ? 2 : 0,
            }}>
            <FormControlLabel
              sx={switchPrimary}
              control={
                <Switch
                  checked={noReplyOnly}
                  onChange={(_, v) => setNoReplyOnly(v)}
                />
              }
              label={
                noReplyOnly
                  ? 'Showing unreplied reviews'
                  : 'Showing all reviews'
              }
            />
            {isAdmin && (
              <Button sx={generateButton} onClick={handleResetReviews}>
                {resetLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  reviewLabels.resetReviews
                )}
              </Button>
            )}
          </Box>
        </Box>

        <Button
          sx={{ mt: 2, width: '180px' }}
          size="small"
          startIcon={<AssessmentOutlinedIcon />}
          variant="outlined"
          onClick={() => navigate('/home/reports')}
          aria-label="Generate report from reviews">
          Generate Report
        </Button>

        <Box mt={8} sx={infoContainer}>
          {conditionalRender(
            loading,
            <Box sx={progressContainer}>
              <CircularProgress size={40} />
            </Box>,
          )}

          {!loading && (
            <Grid sx={gridContainer} container spacing={2}>
              {reviews.length > 0 &&
                reviews.map(review => (
                  <Grid
                    item
                    xs={12}
                    md={isMobile ? 12 : 6}
                    key={review.reviewId}>
                    <ListItem sx={listItem}>
                      <ReviewCard
                        review={review}
                        onPress={handleOnReviewPress}
                      />
                    </ListItem>
                  </Grid>
                ))}

              {reviews.length > 0 && hasMore && (
                <Grid item xs={12} textAlign="center">
                  <Button
                    variant="outlined"
                    onClick={loadMore}
                    disabled={loadingMore}>
                    {loadingMore ? <CircularProgress size={20} /> : 'Load more'}
                  </Button>
                </Grid>
              )}
            </Grid>
          )}

          {!loading && reviews.length === 0 && <NoReviewsPlaceholder />}
        </Box>
      </Box>

      {selectedReview && (
        <ReviewDialog
          review={selectedReview}
          onClose={() => setSelectedReview(undefined)}
        />
      )}
    </Screen>
  );
};

const NoReviewsPlaceholder = () => {
  return (
    <Box sx={noReviewsContainer}>
      <IconNoReviews width={25} height={40} />
      <Typography variant="h6" mt={3} sx={noReviewsTitle}>
        {reviewLabels.noReviewsTitle}
      </Typography>
      <Typography mt={2} sx={noReviewsSubtitle}>
        {reviewLabels.noReviewsSubtitle}
      </Typography>
    </Box>
  );
};

export { ReviewsScreen };
